import type { FC } from 'react';
import { MDText } from 'i18n-react';
import type { NextRouter } from 'next/router';

import { ChipGroup } from '@motorway/mw-highway-code/pre-alpha';

import type { UseAccountRouterReturnType } from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import { triggerCompletePurchasesStatusChipClickedEvent } from 'Services/analytics/events';
import { PurchasesCompleteStatusTypes } from 'Types/purchaseCompleteStatus';
import { formatCount } from 'Utilities/formatters';
import { applyCypressData } from 'Utilities/helpers';
import { useAccountBiddingStatusCounts } from 'Utilities/hooks/account/useAccountBiddingStatusCounts';

import LocalTexts from '../../Filters.texts';

import { getCountEndpointParams, isActive } from './Status.helpers';

import styles from './Status.module.scss';

const LocalT = new MDText(LocalTexts);

type StatusProps = { router: NextRouter } & Pick<
	UseAccountRouterReturnType,
	'changePage' | 'purchasesCompleteStatusType'
>;

const Status: FC<StatusProps> = ({ changePage, purchasesCompleteStatusType, router }) => {
	const { query } = router;

	const params = getCountEndpointParams(query);

	const { data: statusCounts } = useAccountBiddingStatusCounts({
		isFeatureFlagOn: true,
		keepPreviousData: true,
		params,
	});

	const onClickHandler = (status: string) => {
		changePage({
			purchasesCompleteStatusType: status === purchasesCompleteStatusType ? PurchasesCompleteStatusTypes.All : status,
		});
		triggerCompletePurchasesStatusChipClickedEvent({ status });
	};

	return (
		<div className={styles.container} data-testid="statusChipsContainer">
			<ChipGroup
				controlled={{
					onChange: onClickHandler,
					value: purchasesCompleteStatusType || PurchasesCompleteStatusTypes.All,
				}}
				multiple={false}
				options={Object.values(PurchasesCompleteStatusTypes).map((status) => ({
					...applyCypressData(`${cypressIds.accountFilters.status}-${status}`),
					'aria-label': String(
						LocalT.translate(`status.aria`, {
							status: LocalT.translate(`status.chips.${status}`),
						}),
					),
					disabled: statusCounts?.[status] === 0 && !isActive(status, purchasesCompleteStatusType),
					key: status,
					label: LocalT.translate(`status.chips.${status}`),
					onClick: () => onClickHandler(status),
					selected: isActive(status),
					size: 'small',
					subtext: formatCount(statusCounts?.[status]),
					value: status,
				}))}
			/>
		</div>
	);
};

export { Status };
